import { InsertedLastUpdate } from '@shared/interfaces/inserted-updated';
import { BerthBoatAssignment, IBerthBoatAssignment } from '@shared/models/berth-boat-assignment';
import { Pier, PierRaw } from '@shared/models/pier';
import { isWithinInterval } from 'date-fns';
import { IProduct, Product } from '@shared/models/product';
import { BcmCostCenter, BcmCostCenterDto } from '@shared/models/bcm-cost-center';
import { BcmBerthCategory, BcmBerthCategoryDto } from '@shared/models/bcm-berth-category';
import { BcmLock } from '@shared/models/bcm-lock';
import { ElectricMeterProductType } from '@shared/models/electric-meter-product-type';
import { ElectricMeter } from '@shared/models/electric-meter';
import { TolgeeService } from '@core/translation/tolgee.service';

export enum BerthStatus {
    Free = 'free',
    FreeDueToAbsence = 'free-absence',
    Reserved = 'reserved',
    ReservedPartially = 'reserved-partially',
    Occupied = 'occupied',
    OccupiedPartially = 'occupied-partially',
    Locked = 'locked',
    LockedPartially = 'locked-partially',
}

export const berthStatusTranslationKeys = {
    [BerthStatus.Free]: 'bcmBerths.status.free',
    [BerthStatus.FreeDueToAbsence]: 'bcmBerths.status.free-absence',
    [BerthStatus.Reserved]: 'bcmBerths.status.reserved',
    [BerthStatus.ReservedPartially]: 'bcmBerths.status.reserved-partially',
    [BerthStatus.Occupied]: 'bcmBerths.status.occupied',
    [BerthStatus.OccupiedPartially]: 'bcmBerths.status.occupied-partially',
    [BerthStatus.Locked]: 'bcmBerths.status.locked',
    [BerthStatus.LockedPartially]: 'bcmBerths.status.locked-partially',
};

export type BerthTypeFlagProperty =
    | 'isBuoy'
    | 'isDoubleBox'
    | 'isForeign'
    | 'forGuest'
    | 'forLongTermResidents'
    | 'isLiftStation'
    | 'isJetSki'
    | 'isPier'
    | 'isExceptionBerth'
    | 'isSeasonalBerth'
    | 'isCommercialBerth'
    | 'isVBerth';

export interface BerthTypeFlag {
    property: BerthTypeFlagProperty;
    translationKey: string;
}

export const BERTH_TYPE_FLAGS: BerthTypeFlag[] = [
    {property: 'isBuoy', translationKey: 'bcmBerths.form.isBuoy'},
    {property: 'isDoubleBox', translationKey: 'bcmBerths.form.isDoubleBox'},
    {property: 'isForeign', translationKey: 'bcmBerths.form.isForeign'},
    {property: 'forGuest', translationKey: 'bcmBerths.form.forGuest'},
    {property: 'forLongTermResidents', translationKey: 'bcmBerths.form.forLongTermResidents'},
    {property: 'isLiftStation', translationKey: 'bcmBerths.form.isLiftStation'},
    {property: 'isJetSki', translationKey: 'bcmBerths.form.isJetSki'},
    {property: 'isPier', translationKey: 'bcmBerths.form.isPier'},
    {property: 'isExceptionBerth', translationKey: 'bcmBerths.form.isExceptionBerth'},
    {property: 'isSeasonalBerth', translationKey: 'bcmBerths.form.isSeasonalBerth'},
    {property: 'isCommercialBerth', translationKey: 'bcmBerths.form.isCommercialBerth'},
    {property: 'isVBerth', translationKey: 'bcmBerths.form.isVBerth'},
];

export function getSortedBerthTypeFlags(): BerthTypeFlag[] {
    return [...BERTH_TYPE_FLAGS].sort((a, b) =>
        TolgeeService.translate(a.translationKey).localeCompare(TolgeeService.translate(b.translationKey))
    );
}

export interface BerthTenantApiClient {
    id: number;
    name: string;
    activated: boolean;
}

export interface IBerth extends InsertedLastUpdate {
    id: number;
    handle: string;
    // pierHandle: string;
    length: number;
    width: number;
    maxDraft: number;
    boatType: string;
    status: BerthStatus;
    noMatch: boolean;
    noMatchLength: boolean;
    noMatchWidth: boolean;
    noMatchMaxDraft: boolean;
    noMatchBoatType: boolean;
    note?: string;
    assignments: IBerthBoatAssignment[];
    pier: PierRaw;
    isDoubleBox: boolean;
    isForeign: boolean;
    isLiftStation: boolean;
    isJetSki: boolean;
    isPier: boolean;
    isBuoy: boolean;
    forGuest: boolean;
    isExceptionBerth: boolean;
    isSeasonalBerth: boolean;
    isCommercialBerth: boolean;
    isVBerth: boolean;
    forLongTermResidents: boolean;
    multipleOverlappingBookingsAllowed: boolean;
    labelAngle?: number;
    lat: number;
    long: number;
    maxCarryingCapacity: number;
    draftMeasuringStationUuid: string;
    draftMeasurementDifference: number;
    isMostFree: boolean;
    matchFilter?: boolean;
    freeScore?: number;

    products: IProduct[];
    costCenter: BcmCostCenterDto;
    category?: BcmBerthCategoryDto | null;

    reservationEndsToday: boolean;
    reservationEndsWithinNext7Days: boolean;

    locks: BcmLock[];

    defaultElectricMeter: ElectricMeter;
    electricMeterProductType: ElectricMeterProductType;
    electricitySwitchableViaApp: boolean;

    tenantApiClients: BerthTenantApiClient[];

    qrCodePayload?: string;
    qrCodeFileId?: number;
    qrCodeCategory?: string;
}

export class Berth {
    id: number;
    handle: string;
    length: number;
    width: number;
    maxDraft: number;
    boatType: string;
    status: BerthStatus;
    statusIndex: number;
    statusText: string;
    noMatch: boolean;
    noMatchLength: boolean;
    noMatchWidth: boolean;
    noMatchMaxDraft: boolean;
    noMatchBoatType: boolean;
    note?: string;
    assignments: BerthBoatAssignment[] = [];
    activeAssignments: BerthBoatAssignment[] = [];
    futureAssignments: BerthBoatAssignment[] = [];
    pier: Pier;
    isDoubleBox: boolean;
    isForeign: boolean;
    isLiftStation: boolean;
    isJetSki: boolean;
    isPier: boolean;
    isBuoy: boolean;
    forGuest: boolean;
    isExceptionBerth: boolean;
    isSeasonalBerth: boolean;
    isCommercialBerth: boolean;
    isVBerth: boolean;
    forLongTermResidents: boolean;
    multipleOverlappingBookingsAllowed: boolean;
    labelAngle?: number;
    lat: number;
    long: number;
    maxCarryingCapacity: number;
    defaultElectricMeter: ElectricMeter;
    electricMeterProductType: ElectricMeterProductType;
    electricitySwitchableViaApp: boolean;
    tenantApiClients: BerthTenantApiClient[] = [];

    draftMeasuringStationUuid?: string;
    draftMeasurementDifference?: number;
    measuredRelativeDraft?: number;
    isMostFree: boolean;
    matchFilter?: boolean;
    freeScore?: number;

    // helpers
    pierHandle: string;
    boatName = '';
    boatLicensePlate = '';
    boatManufacturer = '';
    boatOwner = '';
    type = '';
    squareMeters: number;

    products: Product[];
    costCenter?: BcmCostCenter;
    category?: BcmBerthCategory | null;
    reservationEndsToday?: boolean;
    reservationEndsWithinNext7Days?: boolean;

    uuid: string;

    locks: BcmLock[] = [];
    activeLocks: BcmLock[] = [];
    futureLocks: BcmLock[] = [];

    /**
     * Build a fresh Berth instance by merging changes onto an existing berth.
     * Use this for optimistic state updates where you have a Berth + Partial.
     *
     * The cast through `unknown` is necessary because the Berth class and the
     * IBerth interface use different runtime shapes for `assignments`/`pier`
     * (class instances vs. raw DTOs). The constructor re-wraps both, so the
     * runtime result is correct.
     */
    static merge(berth: Berth, changes: Partial<Berth>): Berth {
        return new Berth({ ...berth, ...changes } as unknown as IBerth);
    }

    constructor(berth: IBerth = {} as IBerth) {
        this.id = berth.id || null;
        this.handle = berth.handle || null;
        this.pierHandle = berth.pier?.handle || null;
        this.length = berth.length || null;
        this.width = berth.width || null;
        this.maxDraft = berth.maxDraft || null;
        this.boatType = berth.boatType || null;
        this.note = berth.note || null;
        this.assignments = (berth.assignments || []).map(assignment => new BerthBoatAssignment(assignment));
        this.activeAssignments = this.getActiveAssignments();
        this.futureAssignments = this.getFutureAssignments();
        this.pier = berth.pier ? new Pier(berth.pier) : null;
        this.isDoubleBox = !!berth.isDoubleBox;
        this.isForeign = !!berth.isForeign;
        this.isLiftStation = !!berth.isLiftStation;
        this.isJetSki = !!berth.isJetSki;
        this.isPier = !!berth.isPier;
        this.isBuoy = !!berth.isBuoy;
        this.forGuest = !!berth.forGuest;
        this.isExceptionBerth = !!berth.isExceptionBerth;
        this.isSeasonalBerth = !!berth.isSeasonalBerth;
        this.isCommercialBerth = !!berth.isCommercialBerth;
        this.isVBerth = !!berth.isVBerth;
        this.forLongTermResidents = !!berth.forLongTermResidents;
        this.multipleOverlappingBookingsAllowed = !!berth.multipleOverlappingBookingsAllowed;
        this.labelAngle = berth.labelAngle ?? null;
        this.lat = berth.lat;
        this.long = berth.long;
        this.maxCarryingCapacity = berth.maxCarryingCapacity;
        this.maxCarryingCapacity = berth.maxCarryingCapacity;
        this.type = this.getType();
        this.draftMeasuringStationUuid = berth.draftMeasuringStationUuid;
        this.draftMeasurementDifference = berth.draftMeasurementDifference;
        this.status = berth.status; // calculated in back end now

        this.locks = (berth.locks || []).map(lock => new BcmLock(lock));
        this.activeLocks = this.getActiveLocks();
        this.futureLocks = this.getFutureLocks();

        if (this.activeLocks?.length > 0) {
            this.status = BerthStatus.Locked;
        }

        this.tenantApiClients = berth.tenantApiClients ?? [];

        this.squareMeters = this.length * this.width;

        // helper for sorting and labeling
        switch (this.status as BerthStatus) {
            case BerthStatus.Free:
                this.statusIndex = 0;
                break;
            case BerthStatus.FreeDueToAbsence:
                this.statusIndex = 1;
                break;
            case BerthStatus.Reserved:
                this.statusIndex = 2;
                break;
            case BerthStatus.Occupied:
                this.statusIndex = 3;
                break;
            case BerthStatus.Locked:
                this.statusIndex = 4;
                break;
        }

        this.statusText = berthStatusTranslationKeys[this.status];

        this.noMatch = !!berth.noMatch;
        this.noMatchLength = !!berth.noMatchLength;
        this.noMatchWidth = !!berth.noMatchWidth;
        this.noMatchMaxDraft = !!berth.noMatchMaxDraft;
        this.noMatchBoatType = !!berth.noMatchBoatType;
        this.isMostFree = !!berth.isMostFree;
        this.matchFilter = !!berth.matchFilter;
        if (this.matchFilter) {
            this.freeScore = berth.freeScore;
        }

        /* table list search helper */
        this.activeAssignments.forEach(assignment => {
            this.boatName += assignment.boat?.name || '';
            this.boatLicensePlate += assignment.boat?.licensePlate || '';
            this.boatManufacturer += assignment.boat?.manufacturer || '';
            this.boatOwner += (assignment.boat?.owner?.fullName || assignment.boat?.ownerCompany?.fullName) || '';

            this.reservationEndsToday = assignment.reservationEndsToday || null;
            this.reservationEndsWithinNext7Days = assignment.reservationEndsWithinNext7Days || null;
        });

        this.boatName = this.boatName || 'ZZ';
        this.boatLicensePlate = this.boatLicensePlate || 'ZZ';
        this.boatManufacturer = this.boatManufacturer || 'ZZ';
        this.boatOwner = this.boatOwner || 'ZZ';
        this.products = (berth.products || [])
            .map(p => new Product(p))
            .sort((a, b) => a.sortIndex - b.sortIndex);

        // undefined = not part of the payload (partial PUT response), so merges keep the current value;
        // null = cleared, and must reach the API because it only touches keys that are sent
        this.costCenter = berth.costCenter === undefined
            ? undefined
            : (berth.costCenter ? new BcmCostCenter(berth.costCenter) : null);
        this.category = berth.category === undefined
            ? undefined
            : (berth.category ? new BcmBerthCategory(berth.category) : null);

        this.defaultElectricMeter = berth.defaultElectricMeter;
        this.electricitySwitchableViaApp = berth.electricitySwitchableViaApp;
        this.electricMeterProductType = berth.electricMeterProductType;
    }

    toString(): string {
        return [this.handle, this.pier?.handle].join(' - ');
    }

    public isCurrentStartDateBetweenDates(currentBookingStart: Date, start: Date, end: Date): boolean {
        return isWithinInterval(currentBookingStart, {start, end});
    }

    public isCurrentEndDateBetweenDates(currentBookingEnd: Date, start: Date, end: Date): boolean {
        return isWithinInterval(currentBookingEnd, {start, end});
    }

    public getActiveAssignments(): BerthBoatAssignment[] {
        return this.assignments.filter((assignment) => assignment.active);
    }

    public getFutureAssignments(): BerthBoatAssignment[] {
        return this.assignments.filter((assignment) => assignment.future);
    }

    public getActiveLocks(): BcmLock[] {
        return this.locks.filter((lock) => lock.active);
    }

    public getFutureLocks(): BcmLock[] {
        return this.locks.filter((lock) => lock.future);
    }

    public hasActiveAssignments(): boolean {
        return !!this.activeAssignments?.length;
    }

    public get area(): number {
        return this.length * this.width;
    }

    private getType(): string {
        return BERTH_TYPE_FLAGS
            .filter(flag => this[flag.property])
            .map(flag => TolgeeService.translate(flag.translationKey))
            .sort((a, b) => a.localeCompare(b))
            .join(', ');
    }
}
